/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  IconButton,
  Typography,
  Box,
  Tooltip,
  Stepper,
  Step,
  FormControl,
  MenuItem,
  Select,
  Container,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  fetchSubmissionChangelog,
  fetchAdminSubmissionChangelog,
  fetchSubmissionDetails,
} from "../../Services/redux/Application-details/ApplicationDetailsActions";
import { fetchApplicationDetails } from "../../Services/redux/appId-details/AppIdDeatilsActions";
import "./ActivityLog.css";
import Topheader from "../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { fetchUserDetails } from "../../Services/redux/userData/UserDataActions";
import { fetchCognitoUsers } from "../../Services/redux/cognito-users/CognitoUsersActions";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ApplicationDetailsErrorDialog from "../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import { getConfig } from "../../config";

const getLogUserId = (logEntry, fallbackUserId) =>
  logEntry?.userId ||
  logEntry?.createdBy ||
  logEntry?.publisherId ||
  logEntry?.sellerId ||
  logEntry?.ownerId ||
  logEntry?.user?.id ||
  logEntry?.user?.userId ||
  fallbackUserId;

const hasExpandableDetails = (logEntry) =>
  Boolean(
    logEntry?.comment ||
      logEntry?.report?.fileName ||
      logEntry?.report?.extLink ||
      logEntry?.report?.url
  );

const getRoleDisplay = (role, idpLoginFlag, isOemSeller, portal) => {
  const isSellerRole = role === "SELLER";
  if (isSellerRole && (idpLoginFlag === "true" || portal === "domain-approver" || isOemSeller)) {
    return "2P";
  }
  if (isSellerRole && idpLoginFlag === "false" && !isOemSeller) {
    return "3P";
  }
  if (
    role === "ADMIN" ||
    (role === "UNDEFINED" && !isOemSeller && idpLoginFlag === "false")
  ) {
    return "HA";
  }
  if (
    role === "DOMAIN_APPROVER" ||
    (role === "UNDEFINED" && (isOemSeller === "true" || idpLoginFlag === "true"))
  ) {
    return "DA";
  }

  return role;
};

const getRoleColor = (role, isOemSeller, idpLoginFlag) => {
  if (role === "SELLER") {
    return "#7A4AF6";
  }
  if ((role === "ADMIN" || role === "UNDEFINED") && !isOemSeller && idpLoginFlag === "false") {
    return "#2A8BE7";
  }
  return "#555";
};

const getShowToState = (fromState, toState) =>
  fromState === toState ? null : toState;

const getStateColor = (state = "") => {
  if (state.includes("REJECTED") || state.includes("DELISTED") || state.includes("DELETED")) {
    return "#FBC2BC";
  }
  if (state.includes("APPROVED") || state.includes("SUBMITTED") || state.includes("GENERATING")) {
    return "#9CCAF6";
  }
  if (state.includes("PUBLISHED")) {
    return "#98F0C8";
  }
  if (state.includes("UPDATED")) {
    return "#FFE898";
  }

  switch (state) {
    case "DRAFT":
      return "#E8E8E8";
    case "IN_REVIEW":
    case "IN_BETA":
    case "OEM_Review":
      return "#FFE898";
    case "PUBLISHED":
      return "#98F0C8";
    default:
      return "#e0e0e0";
  }
};

const getTextColor = (state = "") =>
  state.includes("REJECTED") || state.includes("DELISTED") || state.includes("DELETED")
    ? "#B50B00"
    : undefined;

const formatState = (state) =>
  state
    ? state.replaceAll("_", " ").toLowerCase().replaceAll(/\b\w/g, (char) => char.toUpperCase())
    : "";

const getStatusMessage = (fromState, toState) => {
  const formattedFromState = formatState(fromState);
  const formattedToState = formatState(toState);

  if (formattedFromState.includes("Approved") || formattedToState.includes("Approved")) {
    return "Your app has been";
  }

  if (
    (formattedFromState.includes("Draft") && formattedToState !== "") ||
    (formattedFromState !== "" && formattedToState.includes("Draft"))
  ) {
    return "Moved from";
  }

  return "Saved as";
};

const getCommentBorder = (comment, isExpanded) =>
  comment && isExpanded ? "1px solid rgba(18, 20, 21, 0.2)" : "none";

const getChangelogDisplayName = (log, roleName, currentPortal, idpLoginFlag) => {
  // VW users (seller VW or domain-approver VW) show userPreferredName, fall back to userId
  if (idpLoginFlag === "true") {
    return log.userPreferredName || log.userId || roleName;
  }
  if (currentPortal === "domain-approver") {
    // For domain-approver portal: show userFirstName from response, fall back to userId
    return log.userFirstName || log.userId || roleName;
  }
  if (currentPortal === "global-admin") {
    if (log.userPortal === "global-admin") return "Harman Admin";
    if (log.userPortal === "domain-approver") return "Domain Approver";
    return log.userFirstName || log.userId || roleName;
  }
  // seller portal non-VW
  if (log.userPortal === "global-admin") return "Harman Admin";
  if (log.userPortal === "domain-approver") return "Domain Approver";
  return log.userFirstName || log.userId || roleName;
};

const shouldFetchCognitoUsers = ({ changeLog, idpLogin, isOemSeller, portal }) =>
  changeLog.length > 0 &&
  ((idpLogin === "false" && isOemSeller === "true") ||
    idpLogin === "true" ||
    portal === "global-admin");

const collectUniqueUserIds = (logs) => {
  const userIds = new Set();
  for (const log of logs) {
    const possibleUserIds = [
      log.userId,
      log.createdBy,
      log.publisherId,
      log.sellerId,
      log.ownerId,
      log.user?.id,
      log.user?.userId,
    ];

    for (const id of possibleUserIds) {
      if (id && typeof id === "string") {
        userIds.add(id);
      }
    }
  }
  return Array.from(userIds);
};

const getReportLinkContent = (report) => {
  if (report?.fileName) {
    return <span>{report.fileName}</span>;
  }

  if (report?.extLink) {
    return (
      <a
        href={report.extLink}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#1976d2",
          display: "inline-flex",
          alignItems: "center",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ textDecoration: "none" }}>View Report</span>
        <svg
          width="15"
          height="14"
          viewBox="0 0 15 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            marginLeft: "4px",
            verticalAlign: "middle",
          }}
        >
          <path
            d="M2.05556 14C1.62778 14 1.26157 13.8477 0.956944 13.5431C0.652315 13.2384 0.5 12.8722 0.5 12.4444V1.55556C0.5 1.12778 0.652315 0.761574 0.956944 0.456944C1.26157 0.152315 1.62778 0 2.05556 0H7.5V1.55556H2.05556V12.4444H12.9444V7H14.5V12.4444C14.5 12.8722 14.3477 13.2384 14.0431 13.5431C13.7384 13.8477 13.3722 14 12.9444 14H2.05556ZM5.71111 9.87778L4.62222 8.78889L11.8556 1.55556H9.05556V0H14.5V5.44444H12.9444V2.64444L5.71111 9.87778Z"
            fill="#121415"
            fillOpacity="0.6"
          />
        </svg>
      </a>
    );
  }

  return (
    <a
      href={report?.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: "#1976d2",
        textDecoration: "underline",
        whiteSpace: "nowrap",
      }}
    >
      Report Link
    </a>
  );
};

const getGivenNameFromContext = ({
  idpLoginFlag,
  isOemSeller,
  logUserId,
  userId,
  getUserFirstName,
  getuserData,
}) => {
  if (idpLoginFlag === "true") {
    const givenName = getUserFirstName(logUserId) || "";
    if (givenName) {
      return givenName;
    }
    if (logUserId === userId) {
      return getuserData?.results?.[0]?.givenName || "";
    }
    return "";
  }

  if (idpLoginFlag === "false" && isOemSeller === "true") {
    return getUserFirstName(logUserId) || "";
  }

  return "";
};

const getSellerRoleName = ({ idpLoginFlag, isOemSeller, portal, givenName }) => {
  if ((idpLoginFlag === "false" && isOemSeller === "true") || portal === "domain-approver") {
    return "2P Seller";
  }
  if (idpLoginFlag === "true") {
    return portal === "seller" ? givenName : "2P Seller";
  }
  if ((idpLoginFlag === "false" && !isOemSeller) || portal === "global-admin") {
    return "3P Seller";
  }
  return "Seller";
};

const getUndefinedRoleName = () =>
  sessionStorage.getItem("isOemSeller") || sessionStorage.getItem("idpLogin") === "true"
    ? "Domain Approver"
    : "Harman Admin";

const getRoleName = ({
  roleName,
  logEntry,
  idpLoginFlag,
  isOemSeller,
  portal,
  userId,
  getUserFirstName,
  getuserData,
}) => {
  const logUserId = getLogUserId(logEntry, userId);
  const givenName = getGivenNameFromContext({
    idpLoginFlag,
    isOemSeller,
    logUserId,
    userId,
    getUserFirstName,
    getuserData,
  });

  if (
    idpLoginFlag === "true" &&
    portal === "domain-approver" &&
    (roleName === "DOMAIN_APPROVER" || roleName === "UNDEFINED")
  ) {
    return givenName;
  }

  if (roleName === "SELLER") {
    return getSellerRoleName({ idpLoginFlag, isOemSeller, portal, givenName });
  }

  if (roleName === "UNDEFINED") {
    return getUndefinedRoleName();
  }

  if (roleName === "ADMIN") {
    return "Harman Admin";
  }

  if (roleName === "DOMAIN_APPROVER") {
    return "Domain Approver";
  }

  return roleName;
};

function ActivityLog() { // NOSONAR
  const truncateText = (text, maxLength = 14) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };
  const [loader, setLoader] = useState(false);
  const { appId, submissionId: paramSubmissionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedSubmissionId, setSelectedSubmissionId] =
    useState(paramSubmissionId);

  const submissionDetails = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );
  const allSubmissionsData = useSelector(
    (state) => state.applicationAllSubmissions.submissions
  );
  const errors = useSelector((state) => state.errors);
  const [expandedRows, setExpandedRows] = useState({});
  const [roleFilter, setRoleFilter] = useState(null);
  const [changeLog, setChangeLog] = useState([]);
  const [rollType, setRollType] = useState("");
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const toggleExpand = (index) => {
    setExpandedRows((prev) => {
      const newState = { ...prev, [index]: !prev[index] };
      return newState;
    });
  };
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );

  const [versionObj, setVersionObj] = useState([]);

  const getuserData = useSelector((state) => state.userDetails.userData);

  // Add cognito users state for domain approvers
  const { users: cognitoUsers } = useSelector(
    (state) => state.cognitoUsers || { users: [], loading: false }
  );

  // Create a mapping of userId to firstName for domain approvers
  const userNameMap = useMemo(() => {
    const map = new Map();

    let usersArray = [];
    if (Array.isArray(cognitoUsers)) {
      usersArray = cognitoUsers;
    } else if (cognitoUsers && Array.isArray(cognitoUsers.data)) {
      usersArray = cognitoUsers.data;
    } else if (cognitoUsers && typeof cognitoUsers === "object") {
      const possibleArrayKeys = ["users", "items", "results", "content"];
      for (const key of possibleArrayKeys) {
        if (Array.isArray(cognitoUsers[key])) {
          usersArray = cognitoUsers[key];
          break;
        }
      }
    }
    if (usersArray.length > 0) {
      usersArray.forEach((user) => {
        if (user && typeof user === "object") {
          const userId =
            user.id || user.userId || user.cognitoUserId || user.username;
          const givenName =
            user.givenName || user.first_name || user.name || user.displayName;
          const preferredName =
            user.preferredUserName ||
            user.preferred_name ||
            user.nickname ||
            user.username;
            const portal = user.portal;
          if (userId && givenName) {
            map.set(userId, { givenName, preferredName, portal });
          }
        }
      });
    }

    return map;
  }, [cognitoUsers]);

  // Function to get user firstName for domain approvers
  const getUserFirstName = useCallback(
    (userId) => {
      if (!userId) return null;

      let totalName = userNameMap.get(userId);

      if(getConfig().REACT_APP_PORTAL === "domain-approver") {   
        for (const [, value] of userNameMap.entries()) {
          if(value.portal==="domain-approver"){
            totalName = value?.givenName;
            return totalName || "";
          }
    }
      }
      else {
        return getuserData?.results?.[0]?.givenName || "";
      }
    },
    [userNameMap, getuserData]
  );

  const [cognitoUserLoader, setCognitoUserLoader] = useState(false);
  const userId = sessionStorage.getItem("userId");
  const idpLogin = sessionStorage.getItem("idpLogin");
  const isOemSeller = sessionStorage.getItem("isOemSeller");
  const isOemAdmin = sessionStorage.getItem("customPortal"); // This will be "oemadmin" or "seller"
  // copy icon for VW user
  const handleCopy = (text) => {
    if (text && typeof text === "string" && text.trim() !== "") {
      navigator.clipboard.writeText(text);
      setCopySnackbarOpen(true);
    } else {
      // If no text is provided, don't copy anything or show an error
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setCopySnackbarOpen(false);
  };

  const handleDownload = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'report';
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to opening in new tab
      globalThis.open(url, '_blank');
    }
  };

  // handling version dropdown
  const handleChange = ($event) => {
    setSelectedSubmissionId($event.target.value);
    callNewSubmissionId($event.target.value);
  };

  const callNewSubmissionId = () => {
    // submission details not fetched on ActivityLog page; changelog is loaded by dedicated useEffect
  };

  const idpLoginFlag = sessionStorage.getItem("idpLogin");

  useEffect(() => {
    sessionStorage.setItem("oemStatusSubmissionId", selectedSubmissionId);
    dispatch(fetchApplicationDetails(appId));
    if (selectedSubmissionId) {
      dispatch(fetchSubmissionDetails(appId, selectedSubmissionId));
    }
  }, [appId, dispatch, selectedSubmissionId]);

  useEffect(() => {
    if (allSubmissionsData && allSubmissionsData.length > 0) {
      setVersionObj(
        allSubmissionsData.map((item) => {
          return item;
        })
      );
    }
  }, [allSubmissionsData]);

  useEffect(() => {
    setSelectedSubmissionId(paramSubmissionId);
  }, [paramSubmissionId]);

  useEffect(() => {
    setLoader(true);
    if (idpLogin === "true") {
      setRollType("Domain approver");
    } else if (isOemAdmin === "oem-admin") {
      setRollType("oem-admin");
    } else if (
      isOemSeller ||
      getConfig().REACT_APP_PORTAL === "domain-approver"
    ) {
      setRollType("Domain approver");
    } else {
      setRollType("Harman admin");
    }
    setLoader(false);
  }, [dispatch]);

  useEffect(() => {
   if(getConfig().REACT_APP_PORTAL === "seller") {
dispatch(fetchUserDetails(sessionStorage.getItem("userId"), sessionStorage.getItem("teamId")));
   }
  }, []);

  // Fetch cognito users for domain approvers AND VW users based on changelog user IDs
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const portal = getConfig().REACT_APP_PORTAL;
        if (!shouldFetchCognitoUsers({ changeLog, idpLogin, isOemSeller, portal })) {
          return;
        }

        setCognitoUserLoader(true);
        const userIdsArray = collectUniqueUserIds(changeLog);

        if (portal === "domain-approver" && userIdsArray.length > 0) {
          const result = dispatch(fetchCognitoUsers());
          setCognitoUserLoader(false);
          return result;
        }

        setCognitoUserLoader(false);
      } catch (error) {
        console.error("ActivityLog: Error fetching cognito users:", error);
        setCognitoUserLoader(false);
      }
    };

    fetchUsers();
  }, [dispatch, idpLogin, isOemSeller, changeLog]);

  const backToApplications = () => {
    if (getConfig().REACT_APP_PORTAL === "seller") {
      navigate(`/ui/developer/applications/${appId}`, {
        state: {
          teamId: sessionStorage.getItem("teamId"),
          submissionId: selectedSubmissionId,
        },
      });
    } else if (getConfig().REACT_APP_PORTAL === "domain-approver") {
      navigate(
        `/ui/domain-approver/applications/${appId}/submission/${selectedSubmissionId}`
      );
    } else if (getConfig().REACT_APP_PORTAL === "global-admin") {
      navigate(
        `/ui/harman-admin/application/${appId}/submission/${selectedSubmissionId}`
      );
    }
  };

  useEffect(() => {
    if (
      submissionDetails &&
      Object.keys(submissionDetails).length > 0 &&
      submissionDetails.changelog &&
      submissionDetails.changelog.length > 0
    ) {
      if (getConfig().REACT_APP_PORTAL === "seller") {
        const newChangeLog = submissionDetails.changelog.map((log) => {
          if (log.role === "UNDEFINED") {
            return {
              ...log,
              role:
                (idpLoginFlag === "false" &&
                  isOemSeller &&
                  "DOMAIN_APPROVER") ||
                (idpLoginFlag === "false" && !isOemSeller && "ADMIN") ||
                (idpLoginFlag === "true" && "DOMAIN_APPROVER"),
            };
          } else {
            return log;
          }
        });
        setChangeLog(newChangeLog);
      } else if (getConfig().REACT_APP_PORTAL !== "global-admin" && getConfig().REACT_APP_PORTAL !== "domain-approver") {
        const reports = submissionDetails.reports || [];
        const newChangeLog = submissionDetails.changelog.map((activityLog) => {
          if (activityLog.reportId === null) {
            return {
              ...activityLog,
              report: null,
            };
          }

          const report = reports.find((r) => r.id === activityLog.reportId);
          return {
            ...activityLog,
            report: report || null,
          };
        });
        setChangeLog(newChangeLog);
      }
      // global-admin and domain-approver use the dedicated admin changelog API
    }
  }, [submissionDetails]);

  useEffect(() => {
    if (getConfig().REACT_APP_PORTAL !== "seller") return;
    if (!appId || !selectedSubmissionId) return;

    const loadChangelog = async () => {
      try {
        setLoader(true)
        
        const data = await fetchSubmissionChangelog(appId, selectedSubmissionId);
        if (Array.isArray(data)) {
          const mapped = data.map((log) => {
            let role = "SELLER";
            if (log.userPortal === "global-admin") role = "ADMIN";
            else if (log.userPortal === "domain-approver") role = "DOMAIN_APPROVER";
            return { ...log, role };
          });
          setChangeLog(mapped);
        }
      } catch (error) {
        console.error("Error fetching submission changelog:", error);
      }
      finally{
        setLoader(false)
      }
    };

    loadChangelog();
  }, [appId, selectedSubmissionId, dispatch]);

  useEffect(() => {
    const portal = getConfig().REACT_APP_PORTAL;
    if (portal !== "global-admin" && portal !== "domain-approver") return;
    if (!appId || !selectedSubmissionId) return;

    const loadAdminChangelog = async () => {
      try {
        setLoader(true)
        const data = await fetchAdminSubmissionChangelog(appId, selectedSubmissionId);
        if (Array.isArray(data)) {
          const mapped = data.map((log) => {
            let role = null;
            if (log.userPortal === "global-admin") role = "ADMIN";
            else if (log.userPortal === "domain-approver") role = "DOMAIN_APPROVER";
            else if (log.userPortal === "seller") role = "SELLER";
            return { ...log, role };
          });
          setChangeLog(mapped);
        }
      } catch (error) {
        console.error("Error fetching admin submission changelog:", error);
      }
      finally{
        setLoader(false)
      }
    };

    loadAdminChangelog();
  }, [appId, selectedSubmissionId]);

  const filteredChangeLog = useMemo(
    () => changeLog.filter((log) => !roleFilter || log.role === roleFilter),
    [changeLog, roleFilter]
  );
  const hasFilteredChangeLog = filteredChangeLog.some(() => true);

  return (
    <>
      <Topheader />
        <Backdrop
          open={loader} 
         sx={(theme) => ({
                color: "#fff",
                zIndex: theme.zIndex.drawer + 1,
              })}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
    {!loader && (
      <Container
        maxWidth="lg"
        sx={{ width: "100% !important", maxWidth: "100% !important" }}
      >
        <Box>
          <Box>
            <div className="container-my-release">
              <div
                className="side-menu-my-release"
                style={{ marginTop: "69px" }}
              >
                <button className="arrow-back" onClick={backToApplications} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ArrowBackIcon style={{ color: " #717273" }} />
                  </div>
                  <div className="my-apps">Back To App Details</div>
                </button>
                {appData && Object.keys(appData).length > 0 && (
                  <ApplicationDetailsSideMenu appData={appData} />
                )}
              </div>
              <div
                className="submission-container-all-release"
                style={
                  submissionDetails.status === "DRAFT"
                    ? { marginLeft: "0" }
                    : { width: "100%", marginLeft: "22%" }
                }
              >
                <div
                  style={{
                    position: "fixed",
                    zIndex: "999",
                    background: "white",
                    width: "85%",
                  }}
                >
                  <div className="listing-header-wrapper-all-release">
                    {submissionDetails &&
                      Object.keys(submissionDetails).length > 0 &&
                      appData &&
                      Object.keys(appData).length > 0 && (
                        <div className="app-icon-my-release-activity-log">
                          <div className="icon-name-activity-log">
                            <img
                              src={submissionDetails.icon.url}
                              alt={submissionDetails.fileName}
                              width={60}
                              height={60}
                              className="icon-name-my-log"
                            />
                            {
                              <div style={{ marginLeft: "5px" }}>
                                <p className="application-name">
                                  {appData.name}
                                </p>
                              </div>
                            }
                          </div>
                        </div>
                      )}
                  </div>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                    style={{
                      borderBottom: "1px solid  #12141533",
                      width: "82%",
                    }}
                  >
                    <Typography
                      className="teams-info-text"
                      sx={{ fontSize: "24px !important" }}
                    >
                      Activity Logs{" "}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <p className="filterText">
                        Version
                      </p>

                      <div className="version-activity-log-select ">
                        {selectedSubmissionId &&
                          selectedSubmissionId.length > 0 &&
                          versionObj.length > 0 &&
                          selectedSubmissionId !== "" && (
                            <FormControl
                              size="small"
                              sx={{
                                minWidth: 120,
                                width: "110%",
                                paddingRight: "1px",
                              }}
                            >
                              <Select
                                defaultValue={selectedSubmissionId}
                                value={selectedSubmissionId}
                                onChange={handleChange}
                                variant="outlined"
                                sx={{
                                  padding: "0px !important",
                                }}
                                renderValue={(selected) => {
                                  // Find the selected item to get its versionName for display
                                  const selectedItem = versionObj.find(
                                    (item) => item.id === selected
                                  );
                                  // Only truncate in the renderValue (front section display)
                                  return selectedItem
                                    ? truncateText(selectedItem.versionName)
                                    : "";
                                }}
                              >
                                {versionObj.map((item) => {
                                  return (
                                    <MenuItem
                                      key={item.id}
                                      value={item.id}
                                      sx={{
                                        marginLeft: "8px !important",
                                        marginRight: "8px !important",
                                        borderRadius: "4px",
                                        fontWeight: "bold",
                                        color: "#0476E2",
                                      }}
                                      title={item.versionName}
                                    >
                                      {item.versionName}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          )}
                      </div>
                      <Typography
                        variant="body2"
                        sx={{ color: "#2A2C2D" }}
                        className="filterText"
                      >
                        Filter by
                      </Typography>

                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={roleFilter || "ALL"}
                          onChange={(e) => {
                            const value = e.target.value;
                            setRoleFilter(value === "ALL" ? null : value);
                          }}
                          displayEmpty
                          sx={{
                            bgcolor: "#FFFFFF",
                            fontSize: "12px",
                            fontWeight: "500",
                            height: "32px",
                            width: "137px",
                            paddingTop: "4px",
                            paddingBottom: "4px",
                            gap: "8px",
                          }}
                        >
                          <MenuItem
                            sx={{
                              marginLeft: "8px !important",
                              marginRight: "8px !important",
                              color: roleFilter === null ? "#0476E2" : "",
                              fontWeight: roleFilter === null ? "bold" : "",
                              borderRadius: "4px",
                            }}
                            value="ALL"
                          >
                            All
                          </MenuItem>
                          ,
                          {((idpLoginFlag === "false" &&
                            isOemSeller === "true") ||
                            getConfig().REACT_APP_PORTAL ===
                              "domain-approver") && [
                            <MenuItem
                              sx={{
                                marginLeft: "8px !important",
                                marginRight: "8px !important",
                                color: roleFilter === "SELLER" ? "#0476E2" : "",
                                fontWeight:
                                  roleFilter === "SELLER" ? "bold" : "",
                                borderRadius: "4px",
                              }}
                              key="2P Seller"
                              value="SELLER"
                            >
                              2P Seller
                            </MenuItem>,
                            <MenuItem
                              sx={{
                                marginLeft: "8px !important",
                                marginRight: "8px !important",
                                color:
                                  roleFilter === "DOMAIN_APPROVER"
                                    ? "#0476E2"
                                    : "",
                                fontWeight:
                                  roleFilter === "DOMAIN_APPROVER"
                                    ? "bold"
                                    : "",
                                borderRadius: "4px",
                              }}
                              key="Domain Approver"
                              value="DOMAIN_APPROVER"
                            >
                              {rollType}
                            </MenuItem>,
                          ]}
                          {((idpLoginFlag === "false" &&
                            !isOemSeller &&
                            getConfig().REACT_APP_PORTAL === "seller") ||
                            getConfig().REACT_APP_PORTAL ===
                              "global-admin") && [
                            <MenuItem
                              sx={{
                                marginLeft: "8px !important",
                                marginRight: "8px !important",
                                color: roleFilter === "SELLER" ? "#0476E2" : "",
                                fontWeight:
                                  roleFilter === "SELLER" ? "bold" : "",
                                borderRadius: "4px",
                              }}
                              key="3P Seller"
                              value="SELLER"
                            >
                              3P Seller
                            </MenuItem>,
                            <MenuItem
                              sx={{
                                marginLeft: "8px !important",
                                marginRight: "8px !important",
                                color: roleFilter === "ADMIN" ? "#0476E2" : "",
                                fontWeight:
                                  roleFilter === "ADMIN" ? "bold" : "",
                                borderRadius: "4px",
                              }}
                              key="Harman Admin"
                              value="ADMIN"
                            >
                              {rollType}
                            </MenuItem>,
                          ]}
                          {idpLoginFlag === "true" &&
                            getConfig().REACT_APP_PORTAL === "seller" && [
                              <MenuItem
                                sx={{
                                  marginLeft: "8px !important",
                                  marginRight: "8px !important",
                                  color:
                                    roleFilter === "SELLER" ? "#0476E2" : "",
                                  fontWeight:
                                    roleFilter === "SELLER" ? "bold" : "",
                                  borderRadius: "4px",
                                }}
                                key="2P Seller"
                                value="SELLER"
                              >
                                2P Seller
                              </MenuItem>,
                              <MenuItem
                                sx={{
                                  marginLeft: "8px !important",
                                  marginRight: "8px !important",
                                  color:
                                    roleFilter === "DOMAIN_APPROVER"
                                      ? "#0476E2"
                                      : "",
                                  fontWeight:
                                    roleFilter === "DOMAIN_APPROVER"
                                      ? "bold"
                                      : "",
                                  borderRadius: "4px",
                                }}
                                key="Domain Approver"
                                value="DOMAIN_APPROVER"
                              >
                                {rollType}
                              </MenuItem>,
                            ]}
                          ,
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </div>
                <Box sx={{ width: "82%", marginTop: "161px !important" }}>
                  {cognitoUserLoader ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "300px",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        color: "#fff",
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                      }}
                    >
                      <CircularProgress color="inherit" />
                    </Box>
                  ) : (
                    <>
                      {changeLog?.length > 0 && hasFilteredChangeLog && (
                          <Stepper orientation="vertical" connector={null}>
                            {filteredChangeLog.map((log, index) => { // NOSONAR
                                  const portal = getConfig().REACT_APP_PORTAL;
                                  const date = new Date(log.eventDate);
                                  const formattedDate = date.toLocaleDateString();
                                  const formattedTime = date.toLocaleTimeString();
                                  const roleDisplay = getRoleDisplay(log.role, idpLoginFlag, isOemSeller, portal);
                                  const roleColor = getRoleColor(log.role, isOemSeller, idpLoginFlag);
                                  const showToState = getShowToState(log.fromState, log.toState);
                                  const fromState = formatState(log.fromState);
                                  const toState = formatState(showToState);
                                  const stepKey = `${log.eventDate || ""}-${log.role || ""}-${log.reportId || ""}-${getLogUserId(log, userId) || ""}`;
                                  const roleName = getRoleName({
                                    roleName: log.role,
                                    logEntry: log,
                                    idpLoginFlag,
                                    isOemSeller,
                                    portal,
                                    userId,
                                    getUserFirstName,
                                    getuserData,
                                  });

                                  let copyUsername = null;
                                  if (idpLoginFlag === "true") {
                                    if (log.userPreferredName) {
                                      // Use API response fields directly for tooltip and copy
                                      copyUsername = log.userGivenName
                                        ? `${log.userGivenName} : ${log.userPreferredName}`
                                        : log.userPreferredName;
                                    } else {
                                      // userPreferredName is null — display shows userId, copy it too
                                      copyUsername = log.userId || roleName;
                                    }
                                  }

                                  const cleanComment = decodeURIComponent(log.comment || "").trim();
                                  const reportLinkContent = getReportLinkContent(log.report);

                                  return (
                                    <Step
                                      key={stepKey}
                                      sx={{
                                        borderRadius: "4px",
                                        marginBottom: expandedRows[index]
                                          ? "8px"
                                          : "0px",
                                        marginTop: expandedRows[index]
                                          ? "8px"
                                          : "0px",

                                        "&:hover": { bgcolor: "#f0f0f0" },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          paddingTop: "0px",

                                          "@media(max-height:1200px)": {
                                            paddingBottom: "7px",
                                          },

                                          height:
                                            sessionStorage.getItem(
                                              "idpLogin"
                                            ) === "true"
                                              ? "95px"
                                              : "80px",
                                          display: "flex",
                                          alignItems: "flex-start",
                                          position: "relative",
                                          borderRadius: 2,
                                          cursor:
                                            hasExpandableDetails(log)
                                              ? "pointer"
                                              : "default",
                                          marginBottom: expandedRows[index]
                                            ? "8%"
                                            : "0",
                                          zIndex: 1,
                                        }}
                                        onClick={() =>
                                          hasExpandableDetails(log) &&
                                          toggleExpand(index)
                                        }
                                      >
                                        <Box
                                          sx={{
                                            pr: 2,
                                            p: 2,
                                            width: "10%",
                                            position: "relative",
                                          }}
                                        >
                                          <Typography className="dateText">
                                            {formattedDate} <br />{" "}
                                            <span className="timeText">
                                              {" "}
                                              at {formattedTime}
                                            </span>
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            mx: 2,
                                            borderLeft: "2px solid #ccc",
                                            height: expandedRows[index]
                                              ? "178px"
                                              : "104px",
                                          }}
                                        >
                                          <svg
                                            width="13"
                                            height="25"
                                            viewBox="0 0 13 13"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            style={{
                                              marginLeft: "-7.2px",
                                              marginTop: "12px",
                                            }}
                                          >
                                            <rect
                                              x="2"
                                              y="2"
                                              width="9"
                                              height="9"
                                              transform="rotate(-45 6.5 6.5)"
                                              fill="#808080"
                                            />
                                          </svg>
                                        </Box>
                                        <Box
                                          sx={{
                                            position: "relative",
                                            mx: 2,
                                            height: "100%",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              left: "50%",
                                              top: 0,
                                              bottom: 0,
                                              width: "2px",
                                              backgroundColor: "#ccc",
                                              transform: "translateX(-50%)",
                                            }}
                                          />
                                        </Box>
                                        <Box
                                          sx={{
                                            gap:
                                              sessionStorage.getItem(
                                                "idpLogin"
                                              ) === "true"
                                                ? "12px"
                                                : "0px",
                                            display: "flex",
                                            flexDirection: "column",
                                            marginTop: "-8px",
                                            paddingTop: "2%",
                                            paddingBottom: "1%",
                                            width: "80%",
                                          }}
                                        >
                                          <div
                                            className="activityLogDetails-usrname-button-expand-attachment"
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  width: "32px",
                                                  height: "32px",
                                                  bgcolor: roleColor,
                                                  borderRadius: "50%",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  color: "#fff",
                                                  fontWeight: "bold",
                                                  fontSize: "14px",
                                                  mr: 1,
                                                }}
                                                className="roleCircle"
                                              >
                                                {roleDisplay}
                                              </Box>
                                              <p
                                                style={{
                                                  fontSize: "16px",
                                                  color: "#5A5B5C",
                                                  fontWeight: "600",
                                                  margin: "0px",
                                                }}
                                              >
                                                {getChangelogDisplayName(log, roleName, portal, idpLoginFlag)}
                                                {idpLoginFlag === "true" && (
                                                  <Tooltip title={copyUsername}>
                                                    <ContentCopyIcon
                                                      sx={{
                                                        fontSize: 18,
                                                        color: "#666",
                                                        cursor: "pointer",
                                                        ml: 1,
                                                      }}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCopy(copyUsername || roleName);
                                                      }}
                                                    />
                                                  </Tooltip>
                                                )}
                                              </p>
                                            </Box>
                                            {(log.comment ||
                                              log.report?.fileName ||
                                              log.report?.extLink ||
                                              log.report?.url) && (
                                              <Box>
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    marginRight: "16px",
                                                    gap: "20px",
                                                  }}
                                                >
                                                  {log.report &&
                                                    (log.report?.fileName ||
                                                      log.report?.extLink ||
                                                      log.report?.url) && (
                                                      <Box size="small" sx={{}}>
                                                        {log.report?.extLink ? (
                                                          <svg
                                                            width="17"
                                                            height="8"
                                                            viewBox="0 0 17 8"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                          >
                                                            <path
                                                              d="M7.9721 8H4.77209C3.66543 8 2.72209 7.61 1.94209 6.83C1.16209 6.05 0.772095 5.10667 0.772095 4C0.772095 2.89333 1.16209 1.95 1.94209 1.17C2.72209 0.39 3.66543 0 4.77209 0H7.9721V1.6H4.77209C4.10543 1.6 3.53876 1.83333 3.07209 2.3C2.60543 2.76667 2.37209 3.33333 2.37209 4C2.37209 4.66667 2.60543 5.23333 3.07209 5.7C3.53876 6.16667 4.10543 6.4 4.77209 6.4H7.9721V8ZM5.57209 4.8V3.2H11.9721V4.8H5.57209ZM9.57209 8V6.4H12.7721C13.4388 6.4 14.0054 6.16667 14.4721 5.7C14.9388 5.23333 15.1721 4.66667 15.1721 4C15.1721 3.33333 14.9388 2.76667 14.4721 2.3C14.0054 1.83333 13.4388 1.6 12.7721 1.6H9.57209V0H12.7721C13.8788 0 14.8221 0.39 15.6021 1.17C16.3821 1.95 16.7721 2.89333 16.7721 4C16.7721 5.10667 16.3821 6.05 15.6021 6.83C14.8221 7.61 13.8788 8 12.7721 8H9.57209Z"
                                                              fill="#121415"
                                                              fillOpacity="0.6"
                                                            />
                                                          </svg>
                                                        ) : (
                                                          // For all other cases (url or fileName), show attachment icon
                                                          <svg
                                                            width="18"
                                                            height="16"
                                                            viewBox="0 0 11 16"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                          >
                                                            <path
                                                              d="M10.5 11C10.5 12.3867 10.0133 13.5667 9.04 14.54C8.06667 15.5133 6.88667 16 5.5 16C4.11333 16 2.93333 15.5133 1.96 14.54C0.986667 13.5667 0.5 12.3867 0.5 11V3.6C0.5 2.6 0.85 1.75 1.55 1.05C2.25 0.35 3.1 0 4.1 0C5.1 0 5.95 0.35 6.65 1.05C7.35 1.75 7.7 2.6 7.7 3.6V10.6C7.7 11.2133 7.48667 11.7333 7.06 12.16C6.63333 12.5867 6.11333 12.8 5.5 12.8C4.88667 12.8 4.36667 12.5867 3.94 12.16C3.51333 11.7333 3.3 11.2133 3.3 10.6V3.2H4.9V10.6C4.9 10.7733 4.95667 10.9167 5.07 11.03C5.18333 11.1433 5.32667 11.2 5.5 11.2C5.67333 11.2 5.81667 11.1433 5.93 11.03C6.04333 10.9167 6.1 10.7733 6.1 10.6V3.6C6.08667 3.04 5.89 2.56667 5.51 2.18C5.13 1.79333 4.66 1.6 4.1 1.6C3.54 1.6 3.06667 1.79333 2.68 2.18C2.29333 2.56667 2.1 3.04 2.1 3.6V11C2.08667 11.9467 2.41333 12.75 3.08 13.41C3.74667 14.07 4.55333 14.4 5.5 14.4C6.43333 14.4 7.22667 14.07 7.88 13.41C8.53333 12.75 8.87333 11.9467 8.9 11V3.2H10.5V11Z"
                                                              fill="#121415"
                                                              fillOpacity="0.6"
                                                            />
                                                          </svg>
                                                        )}
                                                      </Box>
                                                    )}

                                                  <IconButton
                                                    size="small"
                                                    sx={{
                                                      borderRadius: "4px",
                                                    }}
                                                  >
                                                    {expandedRows[index] ? (
                                                      <ExpandLessIcon />
                                                    ) : (
                                                      <ExpandMoreIcon />
                                                    )}
                                                  </IconButton>
                                                </div>
                                              </Box>
                                            )}
                                          </div>

                                          <Box
                                            sx={{
                                              flex: 1,
                                              ml: 2,
                                              color: "#5A5B5C",
                                            }}
                                          >
                                            {log.fromState && (
                                              <Box
                                                className="roleText"
                                                sx={{
                                                  display: "flex",
                                                  gap: 1,
                                                  alignItems: "center",
                                                  fontSize: "15px",
                                                  borderBottom: getCommentBorder(
                                                    log.comment,
                                                    expandedRows[index]
                                                  ),
                                                  paddingBottom: "12px",
                                                  marginLeft: "25px",
                                                  fontWeight: "400",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {getStatusMessage(
                                                  log.fromState,
                                                  showToState
                                                )}{" "}
                                                <Box
                                                  sx={{
                                                    bgcolor: getStateColor(
                                                      log.fromState
                                                    ),
                                                    color: getTextColor(
                                                      log.fromState
                                                    ),
                                                    padding: "5px 8px",
                                                    display: "inline-block",
                                                    borderRadius: "5px",
                                                    fontWeight: "600",
                                                  }}
                                                >
                                                  {fromState}
                                                </Box>
                                                {showToState && (
                                                  <>
                                                    <svg
                                                      width="17"
                                                      height="16"
                                                      viewBox="0 0 17 16"
                                                      fill="none"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                      <path
                                                        d="M12.2279 12L10.8029 10.6L12.4029 9H8.22791V7H12.4029L10.8279 5.4L12.2279 4L16.2279 8L12.2279 12ZM1.22791 9C0.944572 9 0.707072 8.90417 0.515405 8.7125C0.323739 8.52083 0.227905 8.28333 0.227905 8C0.227905 7.71667 0.323739 7.47917 0.515405 7.2875C0.707072 7.09583 0.944572 7 1.22791 7C1.51124 7 1.74874 7.09583 1.94041 7.2875C2.13207 7.47917 2.22791 7.71667 2.22791 8C2.22791 8.28333 2.13207 8.52083 1.94041 8.7125C1.74874 8.90417 1.51124 9 1.22791 9ZM5.22791 9C4.94457 9 4.70707 8.90417 4.51541 8.7125C4.32374 8.52083 4.22791 8.28333 4.22791 8C4.22791 7.71667 4.32374 7.47917 4.51541 7.2875C4.70707 7.09583 4.94457 7 5.22791 7C5.51124 7 5.74874 7.09583 5.94041 7.2875C6.13207 7.47917 6.22791 7.71667 6.22791 8C6.22791 8.28333 6.13207 8.52083 5.94041 8.7125C5.74874 8.90417 5.51124 9 5.22791 9Z"
                                                        fill="#121415"
                                                        fillOpacity="0.6"
                                                      />
                                                    </svg>
                                                    <Box
                                                      sx={{
                                                        bgcolor:
                                                          getStateColor(
                                                            showToState
                                                          ),
                                                        color:
                                                          getTextColor(
                                                            showToState
                                                          ),
                                                        padding: "5px 8px",
                                                        borderRadius: "5px",
                                                        display: "inline-block",
                                                        fontWeight: "600",
                                                      }}
                                                    >
                                                      {toState}
                                                    </Box>
                                                  </>
                                                )}
                                              </Box>
                                            )}

                                            {(log.comment ||
                                              (log.report &&
                                                (log.report.url ||
                                                  log.report.extLink))) &&
                                              expandedRows[index] && (
                                                <Box
                                                  sx={{
                                                    p: 1,
                                                    marginTop: "4px",
                                                    borderRadius: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 2,
                                                    justifyContent:
                                                      "space-between",
                                                    width: "99%",
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      alignItems: "baseline",
                                                      marginTop:
                                                        log.report === null
                                                          ? "0px"
                                                          : "-18px",
                                                      marginLeft: "2px",
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        marginLeft: "14px",
                                                      }}
                                                    >
                                                      <strong>Comment:</strong>{" "}
                                                    </Typography>
                                                    <Typography>
                                                      {cleanComment || "NA"}
                                                    </Typography>
                                                  </Box>

                                                  {log.report &&
                                                    (log.report.url ||
                                                      log.report.extLink) && (
                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          border:
                                                            "1px solid #12141533",
                                                          padding: "8px",
                                                          borderRadius: "4px",
                                                          marginRight: "16px",
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          sx={{
                                                            marginRight: "8px",
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                            flexWrap: "nowrap",
                                                          }}
                                                        >
                                                          <strong>
                                                            Report:
                                                          </strong>
                                                          &nbsp;
                                                          {reportLinkContent}
                                                        </Typography>

                                                        {log.report
                                                          .fileName && (
                                                          <button
                                                            className="download"
                                                            style={{
                                                              color: "inherit",
                                                              width: "32px",
                                                              height: "32px",
                                                              cursor: "pointer",
                                                            }}
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              const downloadUrl = log.report.url || log.report.extLink;
                                                              if (downloadUrl) {
                                                                handleDownload(downloadUrl, log.report.fileName);
                                                              }
                                                            }}
                                                          >
                                                            <FileDownloadOutlinedIcon
                                                              style={{
                                                                color:
                                                                  "white",
                                                                height:
                                                                  "16px",
                                                                width: "16px",
                                                                marginRight: 3,
                                                                marginTop: 2,
                                                              }}
                                                            />
                                                          </button>
                                                        )}
                                                      </Box>
                                                    )}
                                                </Box>
                                              )}
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Step>
                                  );
                                  })}
                          </Stepper>
                      )}
                      {changeLog?.length > 0 && !hasFilteredChangeLog && (
                        <Typography>
                          No data available for the selected filter.
                        </Typography>
                      )}
                      {!changeLog?.length && (
                        <Typography>No changelog available.</Typography>
                      )}
                    </>
                  )}
                </Box>
              </div>
            </div>
            {errors?.errorMessage != null &&
              errors?.errorType != null && <ApplicationDetailsErrorDialog />}
          </Box>
        </Box>
      </Container>
    )}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Text copied!
        </Alert>
      </Snackbar>
    </>
  );
}

export default ActivityLog;
